import { useState } from "react";
import { Box, ButtonBase, Link, Skeleton, Stack, Typography, useTheme } from "@mui/material";
import { ExpandMoreRounded as ChevronIcon } from "@mui/icons-material";
import { Link as RouterLink } from "react-router-dom";
import ErrorDisplay from "../common/ErrorDisplay/ErrorDisplay";
import { hapticError, hapticSelection, hapticSuccess } from "../../lib/haptics";
import { isNative } from "../../lib/platform";
import { resolveNotificationTarget } from "../../lib/notificationTarget";
import { useAnalytics } from "../../hooks/useAnalytics";
import { SavedScreenAlertsGroup } from "../Screener/SavedScreenAlertsGroup";
import { useSavedScreens } from "../Screener/hooks/useSavedScreens";
import { alertsOn } from "../Screener/screenAlertCopy";
import AlertGroupCard from "./AlertGroupCard";
import AlertLevelDialog from "./AlertLevelDialog";
import NotificationAvatar from "./NotificationAvatar";
import NotificationPreferencesCard from "./NotificationPreferencesCard";
import { useNotificationsContext } from "./NotificationsProvider";
import { useMySubscriptions } from "./hooks/useMySubscriptions";
import { useSubscribe } from "./hooks/useSubscribe";
import { useUnsubscribe } from "./hooks/useUnsubscribe";
import { groupSubscriptionsByTopic } from "./subscriptionSelectors";
import { NotificationSubscription, SubscriptionLevel } from "./types";

const ANALYTICS_SOURCE = "manage_alerts";

interface AlertRowProps {
  subscription: NotificationSubscription;
  disabled: boolean;
  onEditLevel: (subscription: NotificationSubscription) => void;
  onTurnOff: (subscription: NotificationSubscription) => void;
}

/**
 * One subscription. The COMPANY NAME is the title and the ticker a secondary
 * label, because the subscription follows the company and a ticker can go
 * stale. Everything shown — name, image, level wording, destination — is what
 * the server sent, so a topic this build has never heard of renders the same.
 */
const AlertRow = ({ subscription, disabled, onEditLevel, onTurnOff }: AlertRowProps) => {
  const theme = useTheme();
  const isDark = theme.palette.mode === "dark";
  const target = resolveNotificationTarget(subscription.path, null, { isNative: isNative() });
  // Only a ticker is worth a second line; any other key is an internal id.
  const showKey =
    subscription.entityKind === "ticker" && subscription.entityKey !== subscription.label;

  const identity = (
    <>
      <NotificationAvatar imageKind={subscription.imageKind} imageValue={subscription.imageValue} />
      <Box sx={{ minWidth: 0 }}>
        <Typography
          noWrap
          className="alert-row-label"
          sx={{ fontSize: "0.9375rem", fontWeight: 600, color: theme.palette.text.primary }}
        >
          {subscription.label}
        </Typography>
        {showKey && (
          <Typography noWrap sx={{ fontSize: "0.8125rem", color: theme.palette.text.secondary }}>
            {subscription.entityKey}
          </Typography>
        )}
      </Box>
    </>
  );

  const identitySx = {
    display: "flex",
    alignItems: "center",
    gap: 1.5,
    flex: "1 1 200px",
    minWidth: 0,
    minHeight: 44,
    borderRadius: 2,
    color: "inherit",
    textDecoration: "none",
  } as const;

  const quietButtonSx = {
    minHeight: 34,
    px: 1.25,
    borderRadius: 1,
    fontFamily: "inherit",
    fontSize: "0.8125rem",
    fontWeight: 500,
    whiteSpace: "nowrap",
    WebkitTapHighlightColor: "transparent",
    transition: "background-color 0.15s ease",
    "@media (pointer: coarse)": { minHeight: 44 },
    "&:focus-visible": { outline: `2px solid ${theme.palette.primary.main}`, outlineOffset: 2 },
    "&.Mui-disabled": { opacity: 0.5 },
  } as const;

  return (
    <Box
      sx={{
        display: "flex",
        alignItems: "center",
        flexWrap: "wrap",
        columnGap: 1.5,
        rowGap: 0.5,
        px: 1.5,
        py: 1,
      }}
    >
      {target.kind === "internal" && (
        <Box
          component={RouterLink}
          to={target.path}
          aria-label={`Open ${subscription.label}`}
          sx={{
            ...identitySx,
            "&:hover .alert-row-label": { textDecoration: "underline" },
            "&:focus-visible": {
              outline: `2px solid ${theme.palette.primary.main}`,
              outlineOffset: 2,
            },
          }}
        >
          {identity}
        </Box>
      )}
      {target.kind !== "internal" && <Box sx={identitySx}>{identity}</Box>}

      {/* Right-aligned whether it sits beside the name or wraps under it (xs) */}
      <Box sx={{ display: "flex", alignItems: "center", gap: 0.5, flexShrink: 0, ml: "auto" }}>
        <ButtonBase
          disabled={disabled}
          aria-haspopup="dialog"
          aria-label={`${subscription.label}: ${subscription.levelLabel}. Change alert level`}
          onClick={() => onEditLevel(subscription)}
          sx={{
            ...quietButtonSx,
            gap: 0.25,
            pr: 0.75,
            color: theme.palette.text.primary,
            border: `1px solid ${theme.palette.divider}`,
            "&:hover": {
              backgroundColor: isDark ? "rgba(255, 255, 255, 0.05)" : "rgba(0, 0, 0, 0.04)",
            },
          }}
        >
          {subscription.levelLabel}
          <ChevronIcon sx={{ fontSize: 18, color: theme.palette.text.secondary }} />
        </ButtonBase>
        <ButtonBase
          disabled={disabled}
          aria-label={`Turn off alerts for ${subscription.label}`}
          onClick={() => onTurnOff(subscription)}
          sx={{
            ...quietButtonSx,
            color: theme.palette.text.secondary,
            "&:hover": {
              color: theme.palette.error.main,
              backgroundColor: isDark ? "rgba(239, 68, 68, 0.1)" : "rgba(239, 68, 68, 0.06)",
            },
          }}
        >
          Turn off
        </ButtonBase>
      </Box>
    </Box>
  );
};

/**
 * Everything the user has asked to be alerted about, grouped by topic, with
 * the level editable and "Turn off" inline. Nothing is ever shown as
 * suspended — subscriptions aren't. Scrolls with the DOCUMENT, so native
 * pull-to-refresh keeps working.
 */
const ManageAlertsPage = () => {
  const theme = useTheme();
  const { capture } = useAnalytics();
  const { openInbox } = useNotificationsContext();
  const { data, isLoading, error, refetch } = useMySubscriptions();
  const subscribe = useSubscribe();
  const unsubscribe = useUnsubscribe();
  const [levelDialog, setLevelDialog] = useState<{
    open: boolean;
    subscription: NotificationSubscription | null;
  }>({ open: false, subscription: null });

  const groups = groupSubscriptionsByTopic(data?.subscriptions ?? []);
  const { data: savedScreens } = useSavedScreens();
  const hasScreenAlerts = (savedScreens?.screens ?? []).some((screen) =>
    alertsOn(screen.alertStatus),
  );
  const showError = Boolean(error) && !data;
  const showEmpty = !isLoading && !showError && groups.length === 0 && !hasScreenAlerts;
  const isPending = subscribe.isPending || unsubscribe.isPending;
  const callbacks = { onSuccess: () => hapticSuccess(), onError: () => hapticError() };

  const handleEditLevel = (subscription: NotificationSubscription) => {
    hapticSelection();
    setLevelDialog({ open: true, subscription });
  };

  // Keep the row while closing so the exit transition keeps its content.
  const closeLevelDialog = () => setLevelDialog((current) => ({ ...current, open: false }));

  const handleSelectLevel = (level: SubscriptionLevel) => {
    const { subscription } = levelDialog;
    closeLevelDialog();
    if (!subscription || subscription.level === level) return;
    capture("alert_level_changed", { topic: subscription.topic, source: ANALYTICS_SOURCE, level });
    subscribe.mutate(
      {
        topic: subscription.topic,
        entityKind: subscription.entityKind,
        entityKey: subscription.entityKey,
        entityRef: subscription.entityRef,
        level,
      },
      callbacks,
    );
  };

  const handleTurnOff = (subscription: NotificationSubscription) => {
    capture("alert_unsubscribed", { topic: subscription.topic, source: ANALYTICS_SOURCE });
    unsubscribe.mutate({ id: subscription.id }, callbacks);
  };

  return (
    <Box
      sx={{
        width: "100%",
        maxWidth: 720,
        mx: "auto",
        // The cards own their padding; the page gives them one shared edge
        px: { xs: 2, sm: 3 },
        pt: { xs: 2, md: 4 },
        // Nothing sits flush against the foot of the page
        pb: 8,
      }}
    >
      <Box
        sx={{
          display: "flex",
          alignItems: "baseline",
          justifyContent: "space-between",
          gap: 2,
          mb: 2,
        }}
      >
        <Typography
          component="h1"
          sx={{
            fontSize: "1.5rem",
            fontWeight: 700,
            letterSpacing: "-0.02em",
            color: theme.palette.text.primary,
          }}
        >
          Manage alerts
        </Typography>
        <Link
          component="button"
          type="button"
          onClick={openInbox}
          underline="hover"
          sx={{
            flexShrink: 0,
            fontSize: "0.875rem",
            fontWeight: 600,
            color: theme.palette.primary.main,
          }}
        >
          View inbox
        </Link>
      </Box>

      {isLoading && (
        <Stack spacing={1} sx={{ mb: 4 }}>
          {[0, 1, 2].map((index) => (
            <Skeleton key={index} variant="rounded" height={56} />
          ))}
        </Stack>
      )}

      {showError && (
        <Box sx={{ mb: 4 }}>
          <ErrorDisplay error={error} onRetry={() => void refetch()} variant="card" fullWidth />
        </Box>
      )}

      {showEmpty && (
        <Box sx={{ py: 6, textAlign: "center" }}>
          <Typography
            sx={{ fontSize: "1rem", fontWeight: 600, color: theme.palette.text.primary }}
          >
            No alerts yet
          </Typography>
          <Typography
            sx={{
              mt: 0.5,
              mx: "auto",
              maxWidth: 420,
              fontSize: "0.875rem",
              color: theme.palette.text.secondary,
            }}
          >
            Open a company, go to its Insiders tab and press Get alerts. We'll let you know when
            its insiders buy or sell.
          </Typography>
        </Box>
      )}

      {groups.map((group) => (
        <AlertGroupCard key={group.topic} title={group.topicLabel}>
          {group.subscriptions.map((subscription) => (
            <AlertRow
              key={subscription.id}
              subscription={subscription}
              disabled={isPending}
              onEditLevel={handleEditLevel}
              onTurnOff={handleTurnOff}
            />
          ))}
        </AlertGroupCard>
      ))}

      <SavedScreenAlertsGroup />

      <NotificationPreferencesCard />

      <AlertLevelDialog
        open={levelDialog.open}
        onClose={closeLevelDialog}
        topic={levelDialog.subscription?.topic ?? ""}
        subject={levelDialog.subscription?.label ?? ""}
        level={levelDialog.subscription?.level ?? null}
        onSave={handleSelectLevel}
      />
    </Box>
  );
};

export default ManageAlertsPage;
